import { tileEquals, type TeamMap } from '../types.js';
import type { DominoRuleSet } from '../variants/types.js';
import {
  DOUBLE_BLANK,
  recordSeatOf,
  seatsOfTeam,
  sumByTeam,
  teamHandValues,
} from './handValue.js';
import { bestTeamSeats } from './standings.js';
import type { HandEndInput, HandEndOutcome, MatchStanding, ScoringPolicy } from './types.js';

/** Optional 13-point opening: a side still on 0 needs at least this much penalty to open. */
export const OPENING_PENALTY_THRESHOLD = 13;

/**
 * Unique lowest-valued team of a «рыба», or null when the lowest total is
 * tied («яйца»). Teams are ranked by their hand values including the
 * 25/50/75 specials — the same numbers they would record — so a lone [6|6]
 * (50) never "wins" a рыба against a side holding 20 pips.
 */
function uniqueLowestTeam(values: readonly number[]): number | null {
  const lowest = Math.min(...values);
  const atLowest = values.flatMap((v, team) => (v === lowest ? [team] : []));
  return atLowest.length === 1 ? (atLowest[0] ?? null) : null;
}

/** Loser that absorbs the carried «яйца» penalty: highest hand value, ties → lowest team index. */
function carryRecipient(values: readonly number[], winnerTeam: number): number | null {
  let recipient: number | null = null;
  let best = -Infinity;
  for (let team = 0; team < values.length; team += 1) {
    const value = values[team] ?? 0;
    if (team !== winnerTeam && value > best) {
      best = value;
      recipient = team;
    }
  }
  return recipient;
}

function opens(ruleSet: DominoRuleSet, teamScore: number, penalty: number): boolean {
  return !ruleSet.opening13 || teamScore > 0 || penalty >= OPENING_PENALTY_THRESHOLD;
}

function eggsOutcome(seatCount: number, values: readonly number[], carried: number): HandEndOutcome {
  const handTotal = values.reduce((sum, v) => sum + v, 0);
  return {
    awarded: new Array<number>(seatCount).fill(0),
    winnerSeats: [],
    blocked: true,
    eggs: true,
    carriedPenalty: carried + handTotal,
    baldFinish: false,
  };
}

/**
 * Settle a Козёл hand. Every losing side records its own hand value against
 * its lowest seat. A carried «яйца» penalty goes to one loser (see
 * `carryRecipient`) and is cleared only once actually written: under the
 * 13-point opening the threshold applies to the loser's full amount (own
 * value + carry), and when that is too small the carry stays pending.
 */
function settleHand(input: HandEndInput): HandEndOutcome {
  const { hands, teamMap, wentOutSeat, ruleSet, scores, carriedPenalty, lastPlayedTile } = input;
  const values = teamHandValues(hands, teamMap);
  const winnerTeam = wentOutSeat !== null
    ? (teamMap.teamOf[wentOutSeat] ?? wentOutSeat)
    : uniqueLowestTeam(values);
  if (winnerTeam === null) return eggsOutcome(hands.length, values, carriedPenalty);

  const teamScores = sumByTeam(scores, teamMap);
  const recipient = carriedPenalty > 0 ? carryRecipient(values, winnerTeam) : null;
  const awarded = new Array<number>(hands.length).fill(0);
  let carryLeft = carriedPenalty;
  for (let team = 0; team < teamMap.teamCount; team += 1) {
    if (team === winnerTeam) continue;
    const carry = team === recipient ? carriedPenalty : 0;
    const penalty = (values[team] ?? 0) + carry;
    if (!opens(ruleSet, teamScores[team] ?? 0, penalty)) continue;
    awarded[recordSeatOf(teamMap, team)] = penalty;
    if (carry > 0) carryLeft = 0;
  }
  const baldFinish = ruleSet.baldFinish
    && wentOutSeat !== null
    && lastPlayedTile !== null
    && tileEquals(lastPlayedTile, DOUBLE_BLANK);
  return {
    awarded,
    winnerSeats: seatsOfTeam(teamMap, winnerTeam),
    blocked: wentOutSeat === null,
    eggs: false,
    carriedPenalty: carryLeft,
    baldFinish,
  };
}

/**
 * Seat scores replaced by their team's total, so `bestTeamSeats` (which
 * ranks a team by its best seat) sees the team's real penalty instead of a
 * partner seat's untouched 0.
 */
function teamTotalsPerSeat(scores: readonly number[], teamMap: TeamMap): number[] {
  const totals = sumByTeam(scores, teamMap);
  return teamMap.teamOf.map((team) => totals[team] ?? 0);
}

/**
 * Козёл: no scoring during play; hands record penalties against the losers;
 * the side reaching the point goal loses and the lowest side wins. A
 * «лысый» finish ends the match via `HandEndOutcome.baldFinish` — the caller
 * ORs it with `isMatchOver`, which only sees scores.
 */
export const PENALTY_SCORING: ScoringPolicy = {
  onPlay(): number {
    return 0;
  },
  onHandEnd: settleHand,
  isMatchOver(scores: readonly number[], ruleSet: DominoRuleSet): boolean {
    return scores.some((s) => s >= ruleSet.pointGoal);
  },
  matchWinnerSeats(standing: MatchStanding): readonly number[] {
    const last = standing.lastResult;
    if (last !== null && last.baldFinish === true) return last.winnerSeats;
    const scores = teamTotalsPerSeat(standing.scores, standing.teamMap);
    return bestTeamSeats({ ...standing, scores }, -1);
  },
  rewardSign: -1,
};
